import { useMemo, useState } from "react";
import { X, Globe2, Info, Search } from "lucide-react";
import { SLIDESHOW_OPTIONS, shortCountry, countryMatches } from "./api5";

const NOW = new Date().getFullYear();
const YEARS = Array.from({ length: NOW - 1900 + 1 }, (_, i) => NOW - i);

function PrefsBody({ prefs, countries, continents, onApply, onReset, onClose }) {
  const [tab, setTab] = useState("continents");
  const [q, setQ] = useState("");
  const [countrySel, setCountrySel] = useState(new Set(prefs.country_codes || []));
  const [contSel, setContSel] = useState(new Set(prefs.continent_codes || []));
  const [from, setFrom] = useState(prefs.date_from_year || "");
  const [to, setTo] = useState(prefs.date_to_year || "");
  const [speed, setSpeed] = useState(prefs.slideshow_seconds || 10);
  const [alwaysDetails, setAlwaysDetails] = useState(!!prefs.always_show_details);

  const toggle = (set, setter) => (code) => {
    const n = new Set(set);
    n.has(code) ? n.delete(code) : n.add(code);
    setter(n);
  };
  const toggleCountry = toggle(countrySel, setCountrySel);
  // When continents change, drop any selected country that no longer belongs to the
  // chosen continent(s) (intersection filter — an out-of-continent country would never
  // match). No pruning while no continent is selected (country-only is valid).
  const toggleCont = (code) => {
    const next = new Set(contSel);
    next.has(code) ? next.delete(code) : next.add(code);
    setContSel(next);
    if (next.size > 0) {
      setCountrySel((prev) => {
        const kept = new Set();
        prev.forEach((cc) => {
          const country = countries.find((x) => x.code === cc);
          if (country && next.has(country.continent_code)) kept.add(cc);
        });
        return kept;
      });
    }
  };

  const filtered = useMemo(() => {
    return countries
      .filter((c) => countryMatches(c, q))
      .slice(0, 400);
  }, [q, countries]);

  const clearAll = () => { setCountrySel(new Set()); setContSel(new Set()); };
  const reset = () => { clearAll(); setFrom(""); setTo(""); };

  // Reset filters (item 2): clear local selections for immediate feedback, then
  // clear the persisted preferences + refresh the stream to the whole world.
  const resetFilters = () => { reset(); onReset?.(); };

  const apply = () => onApply({
    country_codes: [...countrySel],
    continent_codes: [...contSel],
    date_from_year: from ? Number(from) : null,
    date_to_year: to ? Number(to) : null,
    slideshow_seconds: Number(speed),
    always_show_details: alwaysDetails,
  });

  const tokens = [
    ...[...contSel].map((c) => ({ kind: "cont", code: c, label: (continents.find((x) => x.code === c) || {}).name || c })),
    ...[...countrySel].map((c) => ({ kind: "country", code: c, label: shortCountry((countries.find((x) => x.code === c) || {}).name || c) })),
  ];

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 className="ww-panel-title ww-display">Where shall we go?</h2>
        <button className="ww-modal-close" onClick={onClose} data-testid="prefs-close" aria-label="Close"><X size={22} /></button>
      </div>

      <div className="ww-tabs" role="tablist">
        <button role="tab" className={`ww-tab ${tab === "continents" ? "active" : ""}`} data-testid="prefs-tab-continents" onClick={() => setTab("continents")}>Continents</button>
        <button role="tab" className={`ww-tab ${tab === "countries" ? "active" : ""}`} data-testid="prefs-tab-countries" onClick={() => setTab("countries")}>Countries</button>
      </div>

      {tab === "continents" ? (
        <div className="ww-geo-grid" data-testid="prefs-continents">
          {continents.map((c) => (
            <button key={c.code}
              className={`ww-geo-cell ${contSel.has(c.code) ? "selected" : ""} ${c.has_photos ? "" : "muted"}`}
              data-testid={`continent-${c.code}`} onClick={() => toggleCont(c.code)}
              aria-pressed={contSel.has(c.code)}>
              <Globe2 size={22} className="ww-geo-ico" strokeWidth={1.5} />
              {c.name}
            </button>
          ))}
        </div>
      ) : (
        <div data-testid="prefs-countries">
          <div style={{ position: "relative" }}>
            <Search size={16} style={{ position: "absolute", left: 12, top: 13, color: "var(--ww-charcoal-soft)" }} />
            <input className="ww-country-search" style={{ paddingLeft: 36 }} placeholder="Search a country"
              value={q} onChange={(e) => setQ(e.target.value)} data-testid="country-search" />
          </div>
          <div className="ww-country-list">
            {filtered.map((c) => (
              <button key={c.code}
                className={`ww-country-row ${countrySel.has(c.code) ? "selected" : ""} ${c.has_photos ? "" : "disabled"}`}
                data-testid={`country-${c.code}`} onClick={() => toggleCountry(c.code)} aria-pressed={countrySel.has(c.code)}>
                <span className="dot" /> {shortCountry(c.name)}
              </button>
            ))}
          </div>
        </div>
      )}

      {tokens.length > 0 && (
        <div className="ww-selected-row" data-testid="prefs-selected">
          <span className="ww-sel-label">Selected</span>
          {tokens.map((t) => (
            <span key={t.kind + t.code} className="ww-token">
              {t.label}
              <button aria-label={`Remove ${t.label}`} data-testid={`remove-${t.code}`}
                onClick={() => (t.kind === "cont" ? toggleCont(t.code) : toggleCountry(t.code))}><X size={13} /></button>
            </span>
          ))}
          <button className="ww-textlink" style={{ marginLeft: "auto" }} onClick={clearAll} data-testid="prefs-clear-all">Clear all</button>
        </div>
      )}

      <div className="ww-when-row">
        <div className="ww-field ww-when-group">
          <label>When?</label>
          <div className="ww-when-selects">
            <div className="ww-when-sub">
              <span className="ww-when-sublabel">From</span>
              <select className="ww-select ww-year-select" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="prefs-year-from">
                <option value="">Any</option>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <span className="ww-when-dash" aria-hidden="true">–</span>
            <div className="ww-when-sub">
              <span className="ww-when-sublabel">Until</span>
              <select className="ww-select ww-year-select" value={to} onChange={(e) => setTo(e.target.value)} data-testid="prefs-year-to">
                <option value="">Any time</option>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="ww-field">
          <label>Slideshow speed</label>
          <select className="ww-select" value={speed} onChange={(e) => setSpeed(e.target.value)} data-testid="prefs-slideshow">
            {SLIDESHOW_OPTIONS.map((s) => <option key={s} value={s}>{s} seconds</option>)}
          </select>
        </div>
      </div>

      <div className="ww-when-row">
        <div className="ww-field">
          <label>Always display picture details</label>
          <div className="ww-seg" data-testid="prefs-always-details">
            <button type="button" className={`ww-seg-btn ${alwaysDetails ? "active" : ""}`}
              data-testid="prefs-always-details-yes" onClick={() => setAlwaysDetails(true)} aria-pressed={alwaysDetails}>Yes</button>
            <button type="button" className={`ww-seg-btn ${!alwaysDetails ? "active" : ""}`}
              data-testid="prefs-always-details-no" onClick={() => setAlwaysDetails(false)} aria-pressed={!alwaysDetails}>No</button>
          </div>
        </div>
        <div className="ww-field" style={{ alignSelf: "center" }}>
          <span className="ww-seg-note">Keeps the location, date and contributor legend visible in the top-left of the Viewer.</span>
        </div>
      </div>

      <div className="ww-hint"><Info size={14} /> We'll show you photographs taken in these places and time range.</div>

      <div className="ww-prefs-actions">
        <button className="ww-btn ww-btn-ghost" onClick={resetFilters} data-testid="prefs-reset-filters">Reset filters</button>
        <button className="ww-btn ww-btn-primary" onClick={apply} data-testid="prefs-apply">Show Me This World</button>
      </div>
    </>
  );
}

export function Preferences({ open, isMobile, onClose, ...rest }) {
  if (!open) return null;
  if (isMobile) {
    return (
      <div className="ww-modal-overlay" style={{ alignItems: "flex-end", padding: 0 }} onClick={onClose}>
        <div className="ww-panel ww-sheet" onClick={(e) => e.stopPropagation()} data-testid="preferences-sheet">
          <div className="ww-sheet-grip" />
          <PrefsBody onClose={onClose} {...rest} />
        </div>
      </div>
    );
  }
  return (
    <div className="ww-modal-overlay" onClick={onClose}>
      <div className="ww-panel ww-modal" style={{ position: "relative" }} onClick={(e) => e.stopPropagation()} data-testid="preferences-modal">
        <PrefsBody onClose={onClose} {...rest} />
      </div>
    </div>
  );
}
